import {
  DeesElement,
  html,
  customElement,
  type TemplateResult,
  property,
  state,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { viewerStyles } from './styles.js';
import { mediaControls } from '../viewer.styles.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-video-viewer': DeesVideoViewer;
  }
}

@customElement('dees-video-viewer')
export class DeesVideoViewer extends DeesElement {
  public static styles = [themeDefaultStyles, viewerStyles, mediaControls];
  public static demo = demo;
  public static demoGroups = ['Media'];

  @property()
  accessor src: string = '';

  /** Fill an enclosing preview frame. */
  @property({ type: Boolean, reflect: true })
  accessor frameless = false;

  @property()
  accessor poster: string = '';

  @property({ type: Boolean })
  accessor showControls: boolean = true;

  @property({ type: Boolean })
  accessor autoplay: boolean = false;

  @property({ type: Boolean })
  accessor loop: boolean = false;

  @property({ type: Boolean })
  accessor muted: boolean = false;

  @state()
  accessor isPlaying: boolean = false;

  @state()
  accessor currentTime: number = 0;

  @state()
  accessor duration: number = 0;

  @state()
  accessor volume: number = 1;

  @state()
  accessor loading: boolean = true;

  @state()
  accessor error: string = '';

  @state()
  accessor isFullscreen: boolean = false;

  @state()
  accessor controlsVisible: boolean = true;

  @state() private accessor playbackError = '';

  private hideControlsTimer: ReturnType<typeof setTimeout> | null = null;
  private videoElement: HTMLVideoElement | null = null;

  public render(): TemplateResult {
    return html`
      <div
        class="video-container" style=${`--center-visible:${this.isPlaying ? 0 : 1};--center-events:${this.isPlaying ? 'none' : 'auto'};--controls-visible:${this.controlsVisible || !this.isPlaying ? 1 : 0}`}
        @mousemove=${this.handleMouseMove}
        @mouseleave=${this.handleMouseLeave}
      >
        <video
          .src=${this.src}
          .poster=${this.poster}
          .muted=${this.muted}
          .loop=${this.loop}
          ?autoplay=${this.autoplay}
          ?controls=${!this.showControls}
          playsinline
          @loadedmetadata=${this.handleLoadedMetadata}
          @play=${this.handlePlay}
          @pause=${this.handlePause}
          @ended=${this.handleEnded}
          @timeupdate=${this.handleTimeUpdate}
          @error=${this.handleError}
          @waiting=${() => { this.loading = true; }}
          @canplay=${() => { this.loading = false; }}
        ></video>

        ${this.showControls ? html`
          <div class="overlay" @click=${this.handleOverlayClick}>
            <button type="button" class="center-play" aria-label="Play" ?hidden=${this.isPlaying} @click=${(event: Event) => { event.stopPropagation(); this.play(); }}>
              <dees-icon icon="lucide:Play"></dees-icon>
            </button>

            <div class="controls-bar" @click=${(e: Event) => e.stopPropagation()}>
              <input class="media-seek" type="range" aria-label="Playback position" min="0" .max=${String(this.duration || 0)} step="0.1"
                .value=${String(this.currentTime)} ?disabled=${!this.duration}
                @input=${(event: Event) => this.seek(Number((event.target as HTMLInputElement).value))}>


              <div class="controls-row">
                <button type="button" class="toolbar-button" @click=${this.togglePlay} aria-label=${this.isPlaying ? 'Pause' : 'Play'}>
                  <dees-icon icon="lucide:${this.isPlaying ? 'Pause' : 'Play'}"></dees-icon>
                </button>

                <span class="time-display">
                  ${this.formatTime(this.currentTime)} / ${this.formatTime(this.duration)}
                </span>

                <span class="spacer"></span>

                <div class="volume-group">
                  <button type="button" class="toolbar-button" @click=${this.toggleMute} aria-label=${this.muted ? 'Unmute' : 'Mute'}>
                    <dees-icon icon="lucide:${this.muted || this.volume === 0 ? 'VolumeX' : this.volume < 0.5 ? 'Volume1' : 'Volume2'}"></dees-icon>
                  </button>
                  <input
                    class="volume-slider" aria-label="Volume"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    .value=${String(this.muted ? 0 : this.volume)}
                    @input=${this.handleVolumeChange}
                  />
                </div>

                <button type="button" class="toolbar-button" @click=${this.toggleFullscreen} title="Fullscreen">
                  <dees-icon icon="lucide:${this.isFullscreen ? 'Minimize' : 'Maximize'}"></dees-icon>
                </button>
              </div>
            </div>
          </div>
        ` : ''}

        ${this.playbackError ? html`<span class="playback-note" role="status">${this.playbackError}</span>` : ''}
        ${this.loading && !this.error ? html`
          <div class="loading-overlay" role="status" aria-label="Loading video">
            <div class="loading-spinner"></div>
          </div>
        ` : ''}

        ${this.error ? html`
          <div class="error-overlay" role="alert">
            <dees-icon icon="lucide:VideoOff"></dees-icon>
            <span class="error-text">${this.error}</span>
          </div>
        ` : ''}
      </div>
    `;
  }

  public async firstUpdated(): Promise<void> {
    this.videoElement = this.shadowRoot?.querySelector('video') || null;

  }

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    if (!this.isConnected) return;
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    if (this.videoElement && this.src) { this.videoElement.src = this.src; this.videoElement.load(); }
  }

  public async disconnectedCallback(): Promise<void> {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    if (this.hideControlsTimer) clearTimeout(this.hideControlsTimer);
    this.hideControlsTimer = null;
    this.videoElement?.pause();
    this.videoElement?.removeAttribute('src');
    this.videoElement?.load();
    await super.disconnectedCallback();
  }

  public willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (changedProperties.has('src')) {
      this.error = '';
      this.playbackError = '';
      this.loading = !!this.src;
      this.currentTime = 0;
      this.duration = 0;
      this.isPlaying = false;
      this.controlsVisible = true;
      if (this.hideControlsTimer) clearTimeout(this.hideControlsTimer);
    }
  }

  public play(): void {
    const src = this.src;
    this.playbackError = '';
    void this.videoElement?.play().catch(() => {
      if (this.isConnected && this.src === src) this.playbackError = 'Playback could not start. Try again.';
    });
  }

  public pause(): void {
    this.videoElement?.pause();
  }

  public togglePlay(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  public seek(time: number): void {
    if (this.videoElement) {
      this.videoElement.currentTime = Math.max(0, Math.min(this.duration || 0, time));
      this.currentTime = this.videoElement.currentTime;
    }
  }

  public setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.videoElement) {
      this.videoElement.volume = this.volume;
    }
  }

  public toggleFullscreen(): void {
    const container = this.shadowRoot?.querySelector('.video-container') as HTMLElement;
    if (!container) return;

    if (this.isFullscreen) {
      void document.exitFullscreen?.().catch(() => { this.playbackError = 'Could not exit fullscreen.'; });
    } else {
      void container.requestFullscreen?.().catch(() => { this.playbackError = 'Fullscreen is unavailable.'; });
    }
  }

  private handleLoadedMetadata(): void {
    if (this.videoElement) {
      this.duration = Number.isFinite(this.videoElement.duration) ? this.videoElement.duration : 0;
      this.loading = false;
    }
  }

  private handlePlay(): void {
    this.isPlaying = true;
    this.scheduleHideControls();
  }

  private handlePause(): void {
    this.isPlaying = false;
    this.controlsVisible = true;
  }

  private handleEnded(): void {
    this.isPlaying = false;
    this.controlsVisible = true;
  }

  private handleTimeUpdate(): void {
    if (this.videoElement) {
      this.currentTime = this.videoElement.currentTime;
    }
  }

  private handleError(): void {
    if (!this.isConnected || !this.src) return;
    this.error = 'Could not load this video file.';
    this.loading = false;
  }

  private handleOverlayClick(): void {
    this.togglePlay();
  }

  private handleVolumeChange(e: Event): void {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.setVolume(value);
    this.muted = value === 0;
  }

  private toggleMute(): void {
    this.muted = !this.muted;
    if (this.videoElement) {
      this.videoElement.muted = this.muted;
    }
  }

  private handleMouseMove(): void {
    this.controlsVisible = true;
    this.scheduleHideControls();
  }

  private handleMouseLeave(): void {
    if (this.isPlaying) {
      this.controlsVisible = false;
    }
  }

  private scheduleHideControls(): void {
    if (this.hideControlsTimer) {
      clearTimeout(this.hideControlsTimer);
    }
    if (this.isPlaying) {
      this.hideControlsTimer = setTimeout(() => {
        this.controlsVisible = false;
      }, 3000);
    }
  }

  private handleFullscreenChange = (): void => {
    this.isFullscreen = this.shadowRoot?.fullscreenElement === this.shadowRoot?.querySelector('.video-container');
  };

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
